<template>
	<div class="upload-input">
		<button
				v-if="!readonly"
				class="target"
				:disabled="disabled"
				type="button"
				@click="openAttachmentDialog"
		>
			<IconSvg icon="attachment" />
			Bijlagen toevoegen
		</button>

		<ul v-if="claimedAttachments.length > 0 || unclaimedAttachments.length > 0">
			<li
					v-for="attachment in claimedAttachments"
					:key="attachment.uploadId"
			>
				<AttachmentItem
						:claimed-attachment="attachment"
						:readonly="readonly"
						downloadable
						@delete="removeUpload(attachment.uploadId)"
						@download="emit('download', attachment.uploadId)"
				/>
			</li>
			<li
					v-for="attachment in unclaimedAttachments"
					:key="attachment.id"
			>
				<AttachmentItem
						:readonly="readonly"
						:unclaimed-attachment="attachment"
						:downloadable="canDownload(attachment)"
						@delete="removeUpload(attachment.id)"
						@download="emit('download', attachment.id)"
				/>
			</li>
		</ul>
	</div>
</template>

<script setup lang="ts">
	import {computed, ref, watch} from 'vue';
	import type {ComputedRef, Ref} from 'vue';
	import type {ClaimedUpload, UnclaimedUpload} from '~/lib/types/uploads';
	import {useDialog} from '~/composables/useDialog';
	import UploadDialog from '~/composables/uploads/UploadDialog.vue';
	import IconSvg from '~/components/generic/IconSvg.vue';
	import AttachmentItem from '~/composables/uploads/AttachmentItem.vue';

	const emit = defineEmits<{
		// eslint-disable-next-line no-unused-vars
		(tag: 'uploadFinished', id: string, filename: string, upload: UnclaimedUpload): void,
		// eslint-disable-next-line no-unused-vars
		(tag: 'uploadRemoved', id: string): void,
		// eslint-disable-next-line no-unused-vars, @typescript-eslint/unified-signatures
		(tag: 'download', id: string): void,
	}>();

	const props = defineProps<{
		// eslint-disable-next-line no-unused-vars
		canDownload: (upload: UnclaimedUpload) => boolean,
		existingAttachments: ClaimedUpload[],
		disabled?: boolean
		readonly?: boolean
	}>();

	const claimedAttachments: ComputedRef<ClaimedUpload[]> = computed(() => props.existingAttachments);
	const unclaimedAttachments: Ref<UnclaimedUpload[]> = ref([]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let popup: any = {};

	const openAttachmentDialog = () => {
		popup?.open();
	};

	const closeAttachmentDialog = () => {
		popup?.close();
	};

	popup = useDialog<typeof UploadDialog>(UploadDialog, {
		onDownload: (id) => emit('download', id),
		onUploadFinished: (index, filename, upload) => {
			unclaimedAttachments.value.push(upload);
			emit('uploadFinished', index, filename, upload);
		},
		onUploadRemoved: (id) => {
			unclaimedAttachments.value = unclaimedAttachments.value.filter(attachment => attachment.id !== id);
			emit('uploadRemoved', id);
		},
		onDone: () => closeAttachmentDialog(),
		topicIrrelevant: props.readonly,
		canDownload: props.canDownload,
		claimedAttachments,
		unclaimedAttachments
	}, 'UploadDialog');

	const removeUpload = (id: string) => {
		if (props.readonly) {
			return;
		}
		// claimedAttachments will be removed via the reactive value of the store but unclaimed are not
		unclaimedAttachments.value = unclaimedAttachments.value.filter(a => a.id !== id);
		emit('uploadRemoved', id);
	};

	watch(claimedAttachments, (claimed) => {
		unclaimedAttachments.value = unclaimedAttachments.value
			.filter(unclaimed => !claimed.some(claimed => claimed.uploadId === unclaimed.id));
	});
</script>

<style scoped>
	.target {
		color: var(--color-primary);
		cursor: pointer;
		border: none;
		background-color: transparent;

		&:disabled {
			pointer-events: none;
			opacity: 0.5;
		}
	}

	ul {
		list-style: none;
		padding: 20px 0 0;
		margin: 0;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		gap: 10px;
		max-width: 100%;
	}

	li {
		flex: 0 1 28em;
		max-width: 100%;
		overflow: hidden;
	}

	@media screen and (max-width: 767px) {
		ul {
			flex-direction: column;
		}

		li {
			flex: 0 0 auto;
		}
	}
</style>
